// Legal content for MiProse.
// Structured so the LegalPage component can render each section consistently.
// Languages: en, zh-HK / zh-TW (Traditional), zh-CN (Simplified).

const privacyEN = {
  title: "Privacy Policy",
  last_updated: "Last Updated: June 15, 2026",
  intro:
    'Welcome to MiProse (the "Service", "we", "us", or "our"). We are deeply committed to protecting the privacy, secrets, and raw emotional content you entrust to us. This Privacy Policy explains how we collect, protect, and handle your information across our global platform.',
  sections: [
    {
      heading: "1. Information We Collect and How We Use It",
      lede: "To safely deliver your future memories, we collect the minimum required data:",
      bullets: [
        ["Your Name / Nickname:", "To personalize your future letter and the AI response."],
        ["Your Email Address:", "The vital shipping destination where your capsule will be delivered 90, 180, or 365 days later."],
        ["Your Letter Content & Uploaded Photos:", "The sacred emotional core of your capsule."],
        ["Transaction Data:", "Processed securely via Stripe. We do not store your raw credit card numbers on our servers."],
      ],
    },
    {
      heading: "2. Ultimate Data Security & Encryption",
      lede: "Because your letters contain deep personal thoughts, privacy is our top engineering priority:",
      bullets: [
        ["Content Encryption:", "All letter contents are strictly encrypted both in transit and at rest within our secured database. Nobody—including our team—can browse or read your active sealed letters."],
        ["Automated Delivery:", "Your letters are unsealed and compiled strictly by automated code triggers at your chosen future date and midnight timestamp."],
      ],
    },
    {
      heading: "3. AI Processing & Zero-Data Retention Policy",
      lede: 'Your letter content is processed via the Anthropic Claude 3.5 Sonnet API to generate your personalized "Old Friend\'s Reply."',
      bullets: [
        ["No AI Training:", "We utilize enterprise-grade API connections. Your private stories, letters, and emotional vulnerabilities are NEVER stored, retained, or used by us or third-party AI entities to train machine learning models. Your data is processed dynamically in-memory and safely delivered back to you."],
      ],
    },
    {
      heading: "4. Data Retention and Deletion",
      paragraphs: [
        "Your data remains sealed in our secure database until your designated delivery date. Once your capsule is triggered and successfully delivered to your inbox, you may request the complete permanent erasure of your record from our servers at any time by contacting our support channel.",
      ],
    },
    {
      heading: "5. Do you record my location when I write the letter?",
      paragraphs: [
        "To beautifully recreate the temporal atmosphere of the moment you sent your letter, our system notes the rough city and country of origin via your network IP at the exact moment of submission. We never request or track your precise GPS coordinates. This spatial tag is securely sealed as a pure emotional component of your time capsule and will only be shown back to you upon decryption. We strictly pledge never to use this data for any third-party tracking, profiling, or commercial purposes.",
      ],
    },
  ],
};

const privacyZH_HK = {
  title: "隱私政策",
  last_updated: "最後更新日期：2026 年 6 月 15 日",
  intro:
    "歡迎來到「情抒 / MiProse」（以下簡稱「本服務」或「我們」）。我們深知你所託付的信件、秘密與情感具有無上的私密性。本隱私政策旨在說明我們如何在全球平台中保護與處理你的個人資料。",
  sections: [
    {
      heading: "1. 我們收集的資料與使用方式",
      lede: "為了精準交付你對未來的記憶，我們僅收集最核心的必要資訊：",
      bullets: [
        ["你的稱呼／暱稱：", "用於在未來信件與 AI 回信中對你的專屬稱呼。"],
        ["你的電子郵件地址：", "這是最重要的「時間終點站」，用於在 90 天、180 天或 365 天後將信件安全送達。"],
        ["信件內容與上傳照片：", "你所封存的情感核心。"],
        ["交易數據：", "全權由 Stripe 安全處理。我們的伺服器絕不會儲存你的信用卡卡號。"],
      ],
    },
    {
      heading: "2. 極致的數據安全與加密保護",
      lede: "你的迷茫與秘密在我們這裡絕對安全：",
      bullets: [
        ["內容加密：", "所有信件內容在傳輸與儲存時均經過嚴格加密。包括我們團隊在內的任何人，都無法在封存期間主動查閱或讀取你未解封的信件。"],
        ["自動化交付：", "你的信件僅會在到達你指定的未來日期與深夜時，由系統自動代碼觸發解封，無人工干預。"],
      ],
    },
    {
      heading: "3. AI 數據不留痕與零訓練承諾",
      lede: "你的信件內容會經由 Anthropic Claude 3.5 Sonnet API 處理，以生成「老朋友的回信」。",
      bullets: [
        ["絕不流向 AI 訓練：", "我們使用企業級 API 接口。你私密的生活碎片、眼淚與脆弱，絕不會被我們或第三方 AI 機構用於訓練任何機器學習模型。所有數據僅在記憶解封時進行即時動態處理，隨後即安全交付至你的郵箱。"],
      ],
    },
    {
      heading: "4. 數據保留與刪除",
      paragraphs: [
        "在你的時光膠囊解封之前，資料會安全儲存在加密數據庫中。當信件成功發送至你的郵箱後，你隨時可以聯繫我們的客服管道，要求從我們的伺服器中永久、徹底刪除該筆膠囊的所有歷史紀錄。",
      ],
    },
    {
      heading: "5. 你們會記錄我寫信時的位置嗎？",
      paragraphs: [
        "為了在一年後為你完美重現當初寄信時的時空氛圍，在您按下送出時，系統會透過粗略的網絡網路 IP 記錄您當時所在的城市與國家（我們絕對不會讀取或索取您的 GPS 精確定位）。這項資料將作為這封記憶信件的一部份被安全封存，僅在解封時向您展示，我們承諾絕不將此數據用於任何第三方追蹤或商業用途。",
      ],
    },
  ],
};

const privacyZH_CN = {
  title: "隐私政策",
  last_updated: "最后更新日期：2026 年 6 月 15 日",
  intro:
    "欢迎来到「情抒 / MiProse」（以下简称「本服务」或「我们」）。我们深知你所托付的信件、秘密与情感具有无上的私密性。本隐私政策旨在说明我们如何在全球平台中保护与处理你的个人资料。",
  sections: [
    {
      heading: "1. 我们收集的资料与使用方式",
      lede: "为了精准交付你对未来的记忆，我们仅收集最核心的必要资讯：",
      bullets: [
        ["你的称呼／昵称：", "用于在未来信件与 AI 回信中对你的专属称呼。"],
        ["你的电子邮件地址：", "这是最重要的「时间终点站」，用于在 90 天、180 天或 365 天后将信件安全送达。"],
        ["信件内容与上传照片：", "你所封存的情感核心。"],
        ["交易数据：", "全权由 Stripe 安全处理。我们的服务器绝不会储存你的信用卡卡号。"],
      ],
    },
    {
      heading: "2. 极致的数据安全与加密保护",
      lede: "你的迷茫与秘密在我们这里绝对安全：",
      bullets: [
        ["内容加密：", "所有信件内容在传输与储存时均经过严格加密。包括我们团队在内的任何人，都无法在封存期间主动查阅或读取你未解封的信件。"],
        ["自动化交付：", "你的信件仅会在到达你指定的未来日期与深夜时，由系统自动代码触发解封，无人工干预。"],
      ],
    },
    {
      heading: "3. AI 数据不留痕与零训练承诺",
      lede: "你的信件内容会经由 Anthropic Claude 3.5 Sonnet API 处理，以生成「老朋友的回信」。",
      bullets: [
        ["绝不流向 AI 训练：", "我们使用企业级 API 接口。你私密的生活碎片、眼泪与脆弱，绝不会被我们或第三方 AI 机构用于训练任何机器学习模型。所有数据仅在记忆解封时进行即时动态处理，随后即安全交付至你的邮箱。"],
      ],
    },
    {
      heading: "4. 数据保留与删除",
      paragraphs: [
        "在你的时光胶囊解封之前，资料会安全储存在加密数据库中。当信件成功发送至你的邮箱后，你随时可以联系我们的客服管道，要求从我们的服务器中永久、彻底删除该笔胶囊的所有历史记录。",
      ],
    },
    {
      heading: "5. 你们会记录我写信时的位置吗？",
      paragraphs: [
        "为了在一年后为你完美重现当初寄信时的时空氛围，在您按下送出时，系统会通过粗略的网络 IP 记录您当时所在的城市与国家（我们绝对不会读取或索取您的 GPS 精确定位）。这项资料将作为这封记忆信件的一部分被安全封存，仅在解封时向您展示，我们承诺绝不将此数据用于任何第三方追踪或商业用途。",
      ],
    },
  ],
};

const termsEN = {
  title: "Terms of Service",
  last_updated: "Last Updated: June 15, 2026",
  intro:
    "By accessing or using MiProse, you agree to be bound by these Terms of Service. Please read them carefully before sealing a capsule.",
  sections: [
    {
      heading: "1. Nature of Service",
      lede: 'MiProse is an emotional time-capsule platform designed for personal reflection. The "Old Friend\'s Reply" provided upon the capsule\'s unlocking is generated via advanced language models (Anthropic Claude).',
      bullets: [
        ["Disclaimer:", "The AI-generated response is strictly for artistic, emotional, and psychological comfort. It DOES NOT constitute legal, medical, psychiatric, or professional mental health counseling. If you are experiencing severe psychological distress, please seek immediate help from local professional medical institutions."],
      ],
    },
    {
      heading: "2. User Responsibilities & Email Maintenance",
      bullets: [
        ["Email Accuracy:", "You are solely responsible for ensuring that the email address provided is correct, active, and accessible in 90, 180, or 365 days. We are not liable for undelivered memories due to typos, deactivated inboxes, or spam filter settings on your end."],
        ["Content Restrictions:", "You agree not to use the Service to schedule harassment, threats, or illegal text targeting third parties."],
      ],
    },
    {
      heading: "3. Payments, Refunds, and Pricing",
      bullets: [
        ["Dynamic Pricing:", "Pricing varies depending on the selected emotional duration prescription and currency region (USD, HKD, TWD, CNY). All payments are handled securely via Stripe."],
        ["Refund Policy:", "Due to the irreversible hosting, dynamic server execution, and immediate setup costs associated with securing your data tunnel for the future, all transactions are final. Refunds are not provided once a capsule is paid and sealed."],
      ],
    },
    {
      heading: "4. Limitation of Liability",
      paragraphs: [
        "While we utilize highly robust infrastructure (FastAPI, MongoDB, automated cron schedulers), we shall not be held liable for failure of delivery caused by major force majeure events, global internet backbone outages, or third-party email provider server crashes.",
      ],
    },
    {
      heading: "5. Right to Erasure (GDPR / CCPA)",
      paragraphs: [
        "Users under GDPR or CCPA jurisdictions retain the absolute right to erasure. You can request a permanent, irreversible hard-delete of your sealed capsule or draft at any time by emailing postman@miprose.com.",
      ],
    },
  ],
};

const termsZH_HK = {
  title: "服務條款",
  last_updated: "最後更新日期：2026 年 6 月 15 日",
  intro:
    "當你訪問或使用「情抒 / MiProse」時，即代表你同意受本服務條款的約束。在封存信件前，請仔細閱讀以下條款。",
  sections: [
    {
      heading: "1. 服務本質與免責聲明",
      lede: "「情抒」是一個旨在提供個人心靈沉澱與情感封存的時光膠囊平台。膠囊解封時所附帶的「老朋友的回信」是由大語言模型（Anthropic Claude）生成。",
      bullets: [
        ["免責聲明：", "AI 生成的回應純屬藝術創作與情感陪伴。本服務絕不構成任何專業的心理諮商、醫療診斷、法律或醫學建議。若你正遭遇嚴重的心理危機或精神困擾，請務必尋求當地專業醫療機構或心理諮詢師的即時協助。"],
      ],
    },
    {
      heading: "2. 用戶責任與郵箱維護",
      bullets: [
        ["郵箱準確性：", "你須獨自負責確保所填寫的電子郵件地址在未來（90 天、180 天或 365 天後）是正確、處於激活狀態且可正常接收信件的。若因輸入錯誤、郵箱停用或你當地的垃圾郵件過濾機制導致記憶無法送達，本服務不承擔相關責任。"],
        ["內容限制：", "你同意不利用本服務向第三方發送惡意騷擾、恐嚇、或違反當地法律法規的內容。"],
      ],
    },
    {
      heading: "3. 付款、退款與定價",
      bullets: [
        ["動態定價：", "價格根據你所選擇的「情感處方箋時段」及地區貨幣（美金、港幣、新台幣、人民幣）動態調整。所有款項全權由 Stripe 安全處理。"],
        ["退款政策：", "鑒於時光膠囊一經付費封存，系統便會即時啟動獨立的加密數據隧道並鎖定未來伺服器資源，所有交易均為最終交易。膠囊成功封存後，恕不接受退款申請。"],
      ],
    },
    {
      heading: "4. 責任限制",
      paragraphs: [
        "儘管我們採用極為穩健的全棧技術架構（FastAPI、MongoDB 及自動化定時排程器），但對於因不可抗力事件、全球骨幹網路中斷、或第三方郵件接收方伺服器癱瘓所導致的交付延遲或失敗，本服務在法律允許的最大範圍內不承擔損害賠償責任。",
      ],
    },
  ],
};

const termsZH_CN = {
  title: "服务条款",
  last_updated: "最后更新日期：2026 年 6 月 15 日",
  intro:
    "当你访问或使用「情抒 / MiProse」时，即代表你同意受本服务条款的约束。在封存信件前，请仔细阅读以下条款。",
  sections: [
    {
      heading: "1. 服务本质与免责声明",
      lede: "「情抒」是一个旨在提供个人心灵沉淀与情感封存的时光胶囊平台。胶囊解封时所附带的「老朋友的回信」是由大语言模型（Anthropic Claude）生成。",
      bullets: [
        ["免责声明：", "AI 生成的回应纯属艺术创作与情感陪伴。本服务绝不构成任何专业的心理咨询、医疗诊断、法律或医学建议。若你正遭遇严重的心理危机或精神困扰，请务必寻求当地专业医疗机构或心理咨询师的即时协助。"],
      ],
    },
    {
      heading: "2. 用户责任与邮箱维护",
      bullets: [
        ["邮箱准确性：", "你须独自负责确保所填写的电子邮件地址在未来（90 天、180 天或 365 天后）是正确、处于激活状态且可正常接收信件的。若因输入错误、邮箱停用或你当地的垃圾邮件过滤机制导致记忆无法送达，本服务不承担相关责任。"],
        ["内容限制：", "你同意不利用本服务向第三方发送恶意骚扰、恐吓、或违反当地法律法规的内容。"],
      ],
    },
    {
      heading: "3. 付款、退款与定价",
      bullets: [
        ["动态定价：", "价格根据你所选择的「情感处方笺时段」及地区货币（美金、港币、新台币、人民币）动态调整。所有款项全权由 Stripe 安全处理。"],
        ["退款政策：", "鉴于时光胶囊一经付费封存，系统便会即时启动独立的加密数据隧道并锁定未来服务器资源，所有交易均为最终交易。胶囊成功封存后，恕不接受退款申请。"],
      ],
    },
    {
      heading: "4. 责任限制",
      paragraphs: [
        "尽管我们采用极为稳健的全栈技术架构（FastAPI、MongoDB 及自动化定时排程器），但对于因不可抗力事件、全球骨干网络中断、或第三方邮件接收方服务器瘫痪所导致的交付延迟或失败，本服务在法律允许的最大范围内不承担损害赔偿责任。",
      ],
    },
    {
      heading: "5. 被遗忘权（GDPR／CCPA）",
      paragraphs: [
        "受 GDPR 或 CCPA 司法管辖之用户，永久保留其被遗忘权。你可以随时致信 postman@miprose.com，要求对你已封存之胶囊或草稿，执行永久、不可逆转的彻底删除。",
      ],
    },
  ],
};

export const LEGAL_CONTENT = {
  privacy: { en: privacyEN, "zh-HK": privacyZH_HK, "zh-TW": privacyZH_HK, "zh-CN": privacyZH_CN },
  terms:   { en: termsEN,   "zh-HK": termsZH_HK,   "zh-TW": termsZH_HK,   "zh-CN": termsZH_CN },
};

export const pickLegal = (kind, lang) => {
  const map = LEGAL_CONTENT[kind] || LEGAL_CONTENT.privacy;
  return map[lang] || map["en"];
};
